<template>
  <q-card :class="cssClass" class="post">
    <div class="bg-grey-1">
      <post-media
        :post-id="post.id"
        :media="post.media"
        :highlightTranscript="highlightTranscript"
        :aspect-ratio="mediaAspectRatio"
        class="post__media"
      />
    </div>
    <router-link
      v-if="showClient"
      :to="{name: 'client_profile', params: {clientId: clientId}}"
      class="q-pa-sm q-link">
      {{ clientName }}
    </router-link>
    <post-time-bar :post="post" />
    <post-toolbar
      :post="post"
      :actions="actions"
      :group="showGroup"
      :notes="notes"
      :trash="trash"
      @trashed="$emit('trashed', $event)"
    />
    <post-content
      :post="post"
      :highlight="highlightContent"
      class="q-pa-md q-ma-none"
    />
    <q-card-actions v-if="actions">
      <post-actions :post="post" />
    </q-card-actions>
  </q-card>
</template>

<style lang="scss">
@import '../../../styles/quasar.variables';

.post {
  display: flex;
  flex-direction: column;

  .post__content {
    flex-grow: 1;
  }

  @media screen and (min-width: $breakpoint-sm-min) {
    &--scrollable {
      height: 500px;

      .post__media {
        margin: 0 auto;
        max-width: 235px;

        video {
          max-height: 235px;
        }
      }
    }
  }
}
</style>

<script lang="ts">
import { defineComponent } from 'vue';
import { name } from '@/client/client-filter';
import { StringMatchRule } from '@/util/string';
import { Post } from '../../../type';
import PostToolbar from './PostToolbar.vue';
import PostMedia from './PostMedia.vue';
import PostContent from './PostContent.vue';
import PostActions from './PostActions.vue';
import PostTimeBar from './PostTimeBar.vue';
import { PostSearchMatchRule, PostSearchMatchRuleType } from '../search/types';

export default defineComponent({
  emits: ['trashed'],
  components: {
    PostToolbar,
    PostContent,
    PostMedia,
    PostActions,
    PostTimeBar,
  },
  props: {
    post: {
      type: Object as () => Post,
      required: true,
    },
    actions: {
      type: Boolean,
      default: false,
    },
    showGroup: {
      type: Boolean,
      default: true,
    },
    notes: {
      type: Boolean,
      default: false,
    },
    trash: {
      type: Boolean,
      default: false,
    },
    showClient: {
      type: Boolean,
      default: false,
    },
    scrollable: {
      type: Boolean,
      default: false,
    },
    popup: {
      type: Boolean,
      default: false,
    },
    highlight: {
      type: Array as () => PostSearchMatchRule[],
      default: () => [],
    },
  },
  computed: {
    highlightContent(): PostSearchMatchRule[] {
      if (!this.highlight) {
        return [];
      }
      return this.highlight
        .filter((r: PostSearchMatchRule) => (!r.type)
        || (r.type === PostSearchMatchRuleType.CONTENT));
    },
    highlightTranscript(): PostSearchMatchRule[] {
      if (!this.highlight) {
        return [];
      }
      return this.highlight
        .filter((r: PostSearchMatchRule) => (!r.type)
        || (r.type === PostSearchMatchRuleType.TRANSCRIPT));
    },
    cssClass(): string {
      return this.scrollable ? 'post--scrollable' : '';
    },
    clientId(): number {
      return this.post.client.id;
    },
    clientName(): string {
      return name(this.post.client);
    },
    mediaAspectRatio(): number|undefined {
      if (this.scrollable && this.$q.screen.gt.xs) {
        return 1;
      }
      return undefined;
    },
  },
});
</script>
